import React, { useContext, useEffect, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { Header } from '@/components/molecules/front-tablet/header'
import { LoaderContextCreator } from '@/contexts/loader'
import { CalcCashChangerModal } from '@/components/molecules/front-tablet/payment/calc-cash-changer-modal'
import { useSelfCheckOutState } from '@/hooks/use-self-check-out-state'
import { UsageDetailCard } from '@/components/molecules/front-tablet/payment/usage-detail-card'
import { Body } from '@/components/molecules/front-tablet/body'
import { FrontTabletLayout } from '@/components/layouts/front-tablet-layout'
import { useReceiptFormModal } from '@/components/molecules/front-tablet/payment/use-receipt-form-modal'
import { CreditCardFormModal } from '@/components/molecules/front-tablet/payment/credit-card-form-modal'
import { SelectablePaymentMethod } from '@/components/molecules/front-tablet/payment/selectable-payment-method'
import { PaymentResultByCash, PaymentResultByCredit, SelfCheckOutState } from '@/models/self-checkout'
import { paymentTabletCheckoutByCredit, paymentTabletCheckoutByCash } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { CheckoutConfirmDialog } from '@/components/organisms/self-checkout/checkout-confirm-dialog'
import { CREDIT_CARD, CASH_CHANGER, PaymentMethod } from '@/models/payment-method'

export const Payment: React.FC = () => {
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { state, stateErrorHandling, doCheckOut, saveState } = useSelfCheckOutState()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { openReceiptFormModal, ReceiptFormModal } = useReceiptFormModal()

  const [isInitializeComponent, setIsInitializeComponent] = useState(false)
  const [isProcessingCashChanger, setIsProcessingCashChanger] = useState(false)
  const [isShowCreditCardForm, setIsShowCreditCardForm] = useState(false)
  const [isShowCheckoutConfirm, setIsShowCheckoutConfirm] = useState(false)

  const onSubmit = async (paymentResult?: PaymentResultByCash | PaymentResultByCredit) => {
    try {
      let payment: SelfCheckOutState['payment'] = undefined
      if (paymentResult) {
        if (paymentResult.kind === 'credit') {
          payment = {
            ...paymentResult,
            kind: 'credit',
            paymentMethod: paymentResult.cardBrand,
          }
        } else {
          payment = {
            ...paymentResult,
            kind: 'cash',
            paymentMethod: i18n.language === 'ja' ? '現金' : 'Cash',
          }
        }
      }
      setIsLoading(true)
      await doCheckOut(
        {
          ...state,
          payment,
        },
        i18n.language,
      )
      history.replace({ pathname: '/self-checkout/complete' })
    } catch (e) {
      console.error(e)
      alert(t('Please complete the procedure at the front desk'))
      history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
    } finally {
      setIsLoading(false)
    }
  }

  const doPaymentCardToken = async (cardToken: string): Promise<void> => {
    try {
      setIsLoading(true)
      const paymentResult = await paymentTabletCheckoutByCredit({
        hotelId: state.hotelId,
        reservations: state.selectReservation!.selectedReservationAndRooms,
        cardToken,
      })
      await onSubmit(paymentResult)
    } catch (e) {
      errorHandler(e)
      setIsLoading(false)
    }
  }

  const confirmReceipt = async (paymentMethod: PaymentMethod, callback) => {
    const res = await openReceiptFormModal()
    if (!res.isConfirmed) {
      return
    }
    saveState({
      ...state,
      confirm: {
        paymentMethod,
        receiptName: res.billingAddress,
      },
    })
    callback()
  }

  const initializeComponent = async () => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'selectReservation')) {
      return
    }
    setIsInitializeComponent(true)
  }

  const reservationWithSales = useMemo(() => {
    return state.selectReservation?.payment?.usage.reservations.filter(r => r.sales.length || r.salesDiscounts.length) || []
  }, [state.selectReservation])

  useEffect(() => {
    initializeComponent()
  }, [])

  if (!isInitializeComponent || !state.selectReservation?.payment) {
    return <></>
  }

  return (
    <FrontTabletLayout state={state}>
      <Header goToPreviousPage={history.goBack} title={t('Payment details')} />
      <Body>
        <div css={containerStyle}>
          <UsageDetailCard reservationWithSales={reservationWithSales} />
          <div style={{ width: '40%', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div css={amountBlock}>
              <div className="detail-block">
                <div className="price-row">
                  <div>{t('Amount used')}</div>
                  <div className="-price">¥{state.selectReservation.payment.usage.totalPrice.toLocaleString()}</div>
                </div>
                <div className="price-row">
                  <div>{t('Discount')}</div>
                  <div className="-price">¥{state.selectReservation.payment.usage.totalDiscount.toLocaleString()}</div>
                </div>
              </div>
              <div className="total-block">
                <div className="price-row">
                  <div>{t('Amount billed')}</div>
                  <div className="-price">¥{state.selectReservation.payment.usage.totalAmount.toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div css={paymentMethodBlock}>
              <SelectablePaymentMethod
                state={state}
                onCredit={() => {
                  confirmReceipt(CREDIT_CARD, () => history.replace({ pathname: '/self-checkout/payment/terminal/card' }))
                }}
                onCreditForm={() => {
                  confirmReceipt(CREDIT_CARD, () => setIsShowCreditCardForm(true))
                }}
                onUnionPay={() => {
                  confirmReceipt(CREDIT_CARD, () =>
                    history.replace({ pathname: '/self-checkout/payment/terminal/card', search: 'unionPay=1' }),
                  )
                }}
                onQR={() => {
                  confirmReceipt(CREDIT_CARD, () => history.replace({ pathname: '/self-checkout/payment/terminal/qr' }))
                }}
                onCashChanger={() => {
                  confirmReceipt(CASH_CHANGER, () => setIsProcessingCashChanger(true))
                }}
                onFront={() => setIsShowCheckoutConfirm(true)}
              />
            </div>
          </div>
        </div>
      </Body>
      <ReceiptFormModal
        key={
          state.confirm?.receiptName ||
          state.selectReservation?.selectedReservations?.[0]?.memberCompany ||
          state.selectReservation?.selectedReservations?.[0]?.guestName ||
          ''
        }
        billingAddress={
          state.confirm?.receiptName ||
          state.selectReservation?.selectedReservations?.[0]?.memberCompany ||
          state.selectReservation?.selectedReservations?.[0]?.guestName ||
          ''
        }
      />
      <CalcCashChangerModal
        isVisibility={isProcessingCashChanger}
        hotelId={state.hotelId}
        totalPrice={Number(state.selectReservation.payment.usage.totalAmount)}
        doPayment={() =>
          paymentTabletCheckoutByCash({
            hotelId: state.hotelId,
            reservations: state.selectReservation?.selectedReservationAndRooms || [],
            paymentPrice: Number(state.selectReservation?.payment?.usage.totalAmount || 0),
          })
        }
        onCancel={() => setIsProcessingCashChanger(false)}
        onComplete={paymentResult => {
          setIsProcessingCashChanger(false)
          onSubmit(paymentResult)
        }}
      />
      {isShowCreditCardForm && (
        <CreditCardFormModal hotelId={state.hotelId} onCancel={() => setIsShowCreditCardForm(false)} onSubmit={doPaymentCardToken} />
      )}
      {isShowCheckoutConfirm && <CheckoutConfirmDialog message={'Proceed to check-out'} onCancel={() => setIsShowCheckoutConfirm(false)} />}
    </FrontTabletLayout>
  )
}

const containerStyle = css({
  display: 'flex',
  gap: 24,
})
const blockLayout = css({
  backgroundColor: '#ffffff',
  borderRadius: 10,
})

const amountBlock = css(blockLayout, {
  '.price-row': {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: 12,
  },
  '.detail-block': {
    padding: '6px 24px 18px 24px',
    fontSize: 18,
    letterSpacing: 2.1,
    '.-price': { fontSize: 20, letterSpacing: 2.4 },
  },
  '.total-block': {
    borderTop: '1px solid #CCCCCC',
    padding: '9px 24px 24px 24px',
    fontWeight: 'bold',

    fontSize: 18,
    letterSpacing: 2.1,
    '.-price': { fontSize: 24, letterSpacing: 3 },
  },
})
const paymentMethodBlock = css(blockLayout, {
  padding: 24,
})
